import debounce from 'debounce-promise';

import { equalsFilter } from '@ourbranch/meilisearch';
import {
  isRedirectableOffer,
  OFFER_PROPERTY_STREET_ATTRIBUTE,
  offerPropertyStreet,
  REDIRECTABLE_OFFER_ATTRIBUTES,
  REDIRECTABLE_OFFER_SCAN_LIMIT,
  redirectableOfferCutoff
} from '@ourbranch/offer-utils';

import { appConfig } from 'brand-config';
import { isCondensedLayout } from 'core/components/theme/condensed-layout';
import { redirectPropertyOf, shouldRedirectToOffer } from 'core/helpers/offer-redirect';

import awsExports from '../../../aws-exports';
import { getClient as getMeiliSearchClient } from '../../../search/meilisearch';

const OFFER_HISTORY_INDEX = `offer-history-${awsExports.stackeryEnvironmentName}`;

const indexNames = {
  offers: OFFER_HISTORY_INDEX,
  users: `users-${awsExports.stackeryEnvironmentName}`,
  policy: `policysummary-${awsExports.stackeryEnvironmentName}`,
  branchPlusPolicies: `branch-plus-policies-${awsExports.stackeryEnvironmentName}`,
  staffUsers: `staff-users-${awsExports.stackeryEnvironmentName}`
};

/**
 * This function mantains a cache of debounced function based on the provided key, in the case of meilisearch search,the searchIndex
 *
 * The point of this is to debounce only similar request.
 * This was done in response to a bug that Dave reported which was as a result of totally
 * different request getting debounced and essentially returning the same data.
 * For example, parallel request to query offer and customer only triggers a single network request
 *
 * @param {*} debuncedFunc
 * @param {*} timeOut
 */
const debounceWithArgs = (debuncedFunc, timeOut) => {
  const debouncedFuncs = {};

  return (key, ...args) => {
    if (!(key in debouncedFuncs)) {
      debouncedFuncs[key] = debounce(debuncedFunc, timeOut);
    }

    return debouncedFuncs[key](key, ...args);
  };
};

const normalizePhoneNumber = (searchString) => {
  if (searchString.match(/\+*1*\(*(\d{3})\)*-*\s*\(*(\d{3})\)*-*\s*\(*(\d{4})\)*/)) {
    return searchString.replace(/\+*1*\(*(\d{3})\)*-*\s*\(*(\d{3})\)*-*\s*\(*(\d{4})\)*/, '$1$2$3');
  }
  return searchString;
};

const normalizeSearchResponse = (response) => {
  if (response) {
    return {
      nbHits: response.totalHits || response.estimatedTotalHits || response.hits?.length || 0,
      hits: response.hits || []
    };
  }
  return { nbHits: 0, hits: [] };
};

// Users with affinity scoping receive `searchMode === 'exact'` from the backend, which
// flips Meilisearch pagination from `offset`/`limit` (estimatedTotalHits, fluctuates as
// you page — see issue #29915) to `page`/`hitsPerPage` (exact totalHits). Users without
// scoping (e.g. InternalSales) stay on offset/limit because exact + distinct on the
// unrestricted offer-history scope can take 30s+.
//
// Callers must pass `offset` as a multiple of `limit` for exact mode to round-trip
// correctly (every current caller does — see search-store.js). The `limit > 0` guard
// keeps NaN out of the page index if a caller ever passes limit=0.
const buildPaginationParams = (limit, offset, searchMode) => {
  if (searchMode === 'exact' && limit > 0) {
    return { page: Math.floor(offset / limit) + 1, hitsPerPage: limit };
  }
  return { limit, offset };
};

export const debouncedMeiliSearch = debounceWithArgs(async (indexName, searchParams, userPreferences) => {
  const index = getMeiliSearchClient(userPreferences).index(indexName);
  const { query, attributeForDistinct, limit, offset, ...searchParamsRest } = searchParams;

  if (index) {
    return index
      .search(query, {
        ...buildPaginationParams(limit, offset, userPreferences?.searchMode),
        ...searchParamsRest
      })
      .then((response) => normalizeSearchResponse(response));
  }

  return [];
}, 500);

// Bundles the composed Customers-tab queries into a single Meilisearch /multi-search request
// (one round-trip, one tenant-token evaluation). Debounced like debouncedMeiliSearch so rapid
// typing coalesces to a single network call.
const debouncedMultiSearch = debounceWithArgs(async (_key, queries, userPreferences) => {
  const client = getMeiliSearchClient(userPreferences);
  if (!client) {
    return { results: [] };
  }
  return client.multiSearch({ queries });
}, 500);

// --- Customers-tab composition (issue #32899) -------------------------------------------
//
// The Customers tab is scoped for agency users by a Meilisearch tenant-token filter. On the
// `users` index that filter keys off the member's Cognito mailing state (`custom_state`), but
// an agent's license is about POLICY state, so members whose policy state differs from their
// mailing state (border towns, PO boxes, moves) are invisible. The policy-summary index is
// already real-time and already scoped by the rated policy `state` in the same tenant token,
// so we compose the Customers tab from BOTH indexes and merge on accountId instead of moving
// policy data onto the customer record.
//
// Gated on `searchMode === 'exact'`, which the backend sets only for affinity-scoped users
// (see get-user-preferences `getSearchMode`). This keeps unrestricted internal users on the
// original users-only path and keeps the policy scope affinity-narrowed, so `distinct` stays
// cheap (running distinct on the full policy scope can take 30s+ — see #29915).
const CUSTOMER_MERGE_WINDOW = 100;

const buildUserCustomerSearchAttributes = (expandedSearch) => {
  const attributes = [
    'attributes.name',
    'attributes.family_name',
    'Username',
    'attributes.address',
    'attributes.custom_state',
    'attributes.custom_city',
    'attributes.custom_zip'
  ];

  if (expandedSearch) {
    return attributes.concat([
      'attributes.phone_number',
      'attributes.custom_addl_phone_numbers',
      'attributes.email',
      'attributes.custom_affinity_code'
    ]);
  }

  return attributes;
};

// Customer-identifying fields on the policy-summary record. There is no email/phone on a
// policy record, so cross-state members surfaced only here are not searchable by those.
const POLICY_CUSTOMER_SEARCH_ATTRIBUTES = [
  'policyDetails.people.firstName',
  'policyDetails.people.lastName',
  'policyDetails.drivers.firstName',
  'policyDetails.drivers.lastName',
  'policyDetails.location.address',
  'policyDetails.location.city',
  'policyDetails.location.state',
  'policyDetails.location.zip',
  'policyDetails.cars.garageLocation.address',
  'policyDetails.cars.garageLocation.city',
  'policyDetails.cars.garageLocation.state',
  'policyDetails.cars.garageLocation.zip',
  'accountId'
];

// Reshape a policy-summary hit into the customer-record shape the Customers Row renders.
// `people` covers home/condo/renters/farm; `drivers` covers auto; auto location lives on the
// first car's garageLocation.
const normalizePolicySummaryHitToCustomer = (hit) => {
  const details = hit.policyDetails || {};
  const person = details.people?.[0] || details.drivers?.[0] || {};
  const location = details.location || details.cars?.[0]?.garageLocation || {};

  return {
    Username: hit.accountId,
    // Flags a row that came from the policy index because the member's mailing-state record
    // was outside the agent's licensed states. Consumers still read the user-record shape.
    source: 'policy-summary',
    attributes: {
      name: person.firstName || '',
      family_name: person.lastName || '',
      address: location.address || '',
      custom_city: location.city || '',
      custom_state: location.state || hit.state || '',
      custom_zip: location.zip || '',
      phone_number: ''
    },
    // Policy-sourced customers have no Cognito account-create date; fall back to the policy
    // effective date so the "created" column renders (awsDateToDateFormatter → date-fns
    // format() throws on an invalid/absent date).
    UserCreateDate: hit.effectiveDate || hit.endDate
  };
};

// Merge, preferring the richer Cognito user record when a customer appears in both indexes;
// policy-only customers (the cross-state members this fixes) are appended and normalized.
const mergeCustomerHits = (userHits, policyHits) => {
  const seenAccountIds = new Set();
  const mergedHits = [];

  for (const userHit of userHits) {
    if (userHit?.Username && !seenAccountIds.has(userHit.Username)) {
      seenAccountIds.add(userHit.Username);
      mergedHits.push(userHit);
    }
  }

  for (const policyHit of policyHits) {
    const accountId = policyHit?.accountId;
    if (accountId && !seenAccountIds.has(accountId)) {
      seenAccountIds.add(accountId);
      mergedHits.push(normalizePolicySummaryHitToCustomer(policyHit));
    }
  }

  return mergedHits;
};

// One multi-search returns both index result sets in query order. We still merge + dedupe +
// page in memory: two independent offset-paginated sources cannot share a stable page boundary,
// so we pull a bounded window from each at offset 0. CUSTOMER_MERGE_WINDOW caps how deep a
// single query reaches; agency name/account searches sit well inside it (surfacing beyond the
// window would need a server-side union).
const searchCustomersComposed = async ({ query, limit, offset, expandedSearch }, userPreferences) => {
  const pagination = buildPaginationParams(CUSTOMER_MERGE_WINDOW, 0, userPreferences?.searchMode);

  const userSearchAttributes = buildUserCustomerSearchAttributes(expandedSearch);
  const { results } = await debouncedMultiSearch(
    'customers-composed',
    [
      {
        indexUid: indexNames.users,
        q: query,
        matchingStrategy: 'all',
        attributesToSearchOn: userSearchAttributes,
        ...(isCondensedLayout() && { attributesToHighlight: userSearchAttributes }),
        ...pagination
      },
      {
        indexUid: indexNames.policy,
        q: query,
        matchingStrategy: 'all',
        distinct: 'accountId',
        attributesToSearchOn: POLICY_CUSTOMER_SEARCH_ATTRIBUTES,
        ...pagination
      }
    ],
    userPreferences
  );

  const [userResult, policyResult] = results || [];
  const mergedHits = mergeCustomerHits(userResult?.hits || [], policyResult?.hits || []);

  return { nbHits: mergedHits.length, hits: mergedHits.slice(offset, offset + limit) };
};

export const searchForWebUserId = async (webUserId, userPreferences) => {
  const indexName = OFFER_HISTORY_INDEX;

  const searchParameters = {
    ...buildPaginationParams(250, 0, userPreferences?.searchMode),
    attributesToSearchOn: ['webUserId'],
    matchingStrategy: 'all'
  };

  const index = getMeiliSearchClient(userPreferences).index(indexName);
  if (index) {
    try {
      const res = await index.search(webUserId, searchParameters).then((response) => normalizeSearchResponse(response));
      return res;
    } catch {
      return [];
    }
  }
  return [];
};

// Excludes dev/support troubleshooting offers. `NOT x = true` (rather than `x = false`) so
// documents indexed before the field existed — which simply lack it — still match (#30994).
const EXCLUDE_TROUBLESHOOTING_FILTER = 'NOT quote.global.troubleshootingMode = true';

// The newest viewable saved-over (parentId) offer for the same customer, or null when the offer being
// viewed should stay put (#30994, #35043). Whether that offer wins — strictly newer, or any age
// when the current offer is a fresh partner-created quote — lives in shouldRedirectToOffer.
// Never-updated quotes are never targets. Errors rethrow so callers can fall back to the backend
// query.
//
// An offer must have options to view — optionless/rejected offers are skipped so the redirect
// never sends staff to a dead-end offer — and must be quoting the SAME set of policy types (#34790).
// A customer can hold two genuinely different concurrent offers — e.g. a FarmHome (Z) quote on one
// property and a FarmRental (Y) quote on another — and both are staff-edited under one webUserId.
// Redirecting between them threw agents onto the wrong quote, which then got edited: their finished
// Home quote appeared to turn into the Rental. An offer is only "the same quote, further along" if
// it is offering the same thing.
export const searchForNewerStaffEditedOffer = async (currentOffer, userPreferences) => {
  const index = getMeiliSearchClient(userPreferences).index(OFFER_HISTORY_INDEX);
  if (!index) {
    return null;
  }

  // #36618: a farm-system quote only redirects to a newer revision of the same property. The street
  // narrows the search server-side, so the other property's revisions can't fill the candidate page;
  // the full property key (street, unit, city, state, zip) decides.
  const property = redirectPropertyOf(currentOffer);
  const propertyFilter = property
    ? ` AND ${equalsFilter(OFFER_PROPERTY_STREET_ATTRIBUTE, offerPropertyStreet(property.address))}`
    : '';
  const { hits } = await index.search('', {
    filter: `${equalsFilter('webUserId', currentOffer.webUserId)} AND parentId EXISTS AND ${EXCLUDE_TROUBLESHOOTING_FILTER} AND updatedDateTime >= "${redirectableOfferCutoff()}"${propertyFilter}`,
    // Meilisearch sorts server-side (updatedDateTime is sortable); we only scan the top page.
    sort: ['updatedDateTime:desc'],
    limit: REDIRECTABLE_OFFER_SCAN_LIMIT,
    attributesToRetrieve: [
      ...REDIRECTABLE_OFFER_ATTRIBUTES,
      'webUserId',
      ...(property ? ['quote.submittedAddress'] : [])
    ]
  });
  const canonicalOffer = hits.find((hit) => isRedirectableOffer(hit, currentOffer.options, property?.key));

  if (!shouldRedirectToOffer({ currentOffer, candidate: canonicalOffer })) {
    return null;
  }

  return {
    id: canonicalOffer.id,
    webUserId: canonicalOffer.webUserId,
    updatedDateTime: canonicalOffer.updatedDateTime,
    parentId: canonicalOffer.parentId
  };
};

const meiliSearch = async (
  { searchString, index, limit = 10, offset = 0, expandedSearch, showOfferHistory, excludeTroubleshootingOffers },
  userPreferences
) => {
  const isOfferSearch = index === 'offers';
  const indexName = indexNames[index];

  let query = searchString;
  if (index === 'users') {
    // if someone searches by phone number, it's best to search just by the numbers:
    query = normalizePhoneNumber(searchString);
  }

  // Affinity-scoped agency users get the Customers tab composed across the users and
  // policy-summary indexes so members whose policy state differs from their mailing state are
  // still visible (issue #32899). Everyone else keeps the original users-only search.
  if (index === 'users' && userPreferences?.searchMode === 'exact') {
    return searchCustomersComposed({ query, limit, offset, expandedSearch }, userPreferences);
  }

  const searchParameters = {
    query,
    limit,
    offset,
    matchingStrategy: 'all'
  };

  if (index === 'users') {
    searchParameters.attributesToSearchOn = buildUserCustomerSearchAttributes(expandedSearch);
  }

  if (isOfferSearch) {
    searchParameters.attributesToSearchOn = [
      'quote.fname',
      'quote.lname',
      'quote.drivers.firstName',
      'quote.drivers.lastName',
      'quote.submittedAddress.address',
      'quote.submittedAddress.city',
      'quote.submittedAddress.state',
      'quote.submittedAddress.zip',
      'quote.rep',
      'webUserId'
    ];

    if (expandedSearch) {
      searchParameters.attributesToSearchOn = searchParameters.attributesToSearchOn.concat([
        'quote.email',
        'quote.phone',
        'quote.global.affinity'
      ]);
    }
  }

  if (index === 'policy') {
    searchParameters.attributesToHighlight = ['*'];
  }

  // The condensed layout's "Matched on" column reads `_formatted`. Highlighting only the searched attributes is a
  // query-time option (no index settings change) and keeps the response small.
  if ((index === 'users' || isOfferSearch) && isCondensedLayout()) {
    searchParameters.attributesToHighlight = searchParameters.attributesToSearchOn;
  }

  if (index === 'branchPlusPolicies') {
    searchParameters.attributesToSearchOn = ['address', 'state', 'customer_first_name', 'customer_last_name'];

    if (expandedSearch) {
      searchParameters.attributesToSearchOn = searchParameters.attributesToSearchOn.concat([
        'phone_number',
        'email',
        'policy_id',
        'account_id'
      ]);
    }
  }

  // Offer search collapses a customer's revisions to one row — every offer update mints a new offer
  // id, so without this a search is pages of the same quote. `webUserId` does that, but it also
  // collapses two genuinely different concurrent offers: on farm brands a customer can hold a
  // FarmHome (Z) quote on one property and a FarmRental (Y) quote on another, and one of them
  // simply disappeared from search — agents then opened whichever survived, edited it, and their
  // other quote appeared to turn into it (#34790). Farm brands dedupe on `offerSearchDedupeKey`
  // (webUserId + the offer's policy types) instead, which still collapses revisions.
  //
  // Branch stays on `webUserId`: the attribute only exists on reindexed documents, and MeiliSearch
  // returns every document that lacks the distinct attribute undeduped — on Branch's ~10.7M-doc
  // index that would mean an unusable offer search until the rebuild finished.
  if (isOfferSearch && !showOfferHistory) {
    searchParameters.distinct = appConfig?.featureFlags?.offerSearchDedupesByProductSet
      ? 'offerSearchDedupeKey'
      : 'webUserId';
  }

  // Canonical users (agencies, sales, UW) never see troubleshooting offers; troubleshooters do.
  // Compose (rather than assign) so any future offer-search filter isn't silently clobbered.
  if (isOfferSearch && excludeTroubleshootingOffers) {
    searchParameters.filter = searchParameters.filter
      ? `${searchParameters.filter} AND ${EXCLUDE_TROUBLESHOOTING_FILTER}`
      : EXCLUDE_TROUBLESHOOTING_FILTER;
  }

  return debouncedMeiliSearch(indexName, searchParameters, userPreferences);
};

export const runSearch = async (
  { searchString, index, limit = 10, offset = 0, expandedSearch, showOfferHistory, excludeTroubleshootingOffers },
  userPreferences
) => {
  return meiliSearch(
    { searchString, index, limit, offset, expandedSearch, showOfferHistory, excludeTroubleshootingOffers },
    userPreferences
  );
};
